import { Flex, Text, Button } from '@nula/core-ui';
import { useSubscription } from './use-subscription';
import styles from './plan-indicator.module.css';

interface PlanIndicatorProps {
  /** `card` = free/trial upgrade card (null for paid); `chip` = paid tier chip (null for free/trial). */
  variant: 'card' | 'chip';
  /** Open the Subscription settings tab (from the free/trial upgrade card). */
  onUpgrade: () => void;
  collapsed?: boolean;
}

/**
 * Sidebar plan surface, split by placement:
 * - `card` (above the profile): a prominent upgrade card for free/basic and
 *   trialing workspaces (not yet paying) — where the sidebar space earns its
 *   keep. Renders nothing for paid workspaces.
 * - `chip` (inline in the profile row): a quiet tier chip for paid workspaces.
 *   Renders nothing for free/trial (the card covers that state).
 */
export function PlanIndicator({ variant, onUpgrade, collapsed = false }: PlanIndicatorProps) {
  const { data: subscription } = useSubscription();
  if (!subscription) return null;

  // "Not yet paying" = free Basic, or on a card-less trial.
  const notPaying = subscription.planCode === 'basic' || subscription.status === 'trialing';

  if (variant === 'chip') {
    if (notPaying) return null;
    return (
      <span data-testid="plan-chip" className={styles.chip}>
        {subscription.planName}
      </span>
    );
  }

  // variant === 'card'
  if (!notPaying) return null;

  // The collapsed (icon-only) sidebar has no room for the card — hide it there.
  if (collapsed) return null;

  const heading =
    subscription.status === 'trialing'
      ? `${subscription.planName} trial`
      : `You're on ${subscription.planName}`;
  const sub =
    subscription.status === 'trialing'
      ? 'Upgrade before your trial ends to keep your plan.'
      : 'Unlock more credits, priority AI & unlimited notes.';
  return (
    <Flex vertical gap="xs" data-testid="plan-upgrade-card" className={styles.upgradeCard}>
      <Text size="sm" className={styles.upgradeHeading}>
        {heading}
      </Text>
      <Text size="xs" type="secondary" className={styles.upgradeSub}>
        {sub}
      </Text>
      <Button variant="primary" block onClick={onUpgrade} className={styles.upgradeButton}>
        Upgrade
      </Button>
    </Flex>
  );
}
